import React, { useCallback, useEffect, useMemo, useRef } from "react"
import { ReactFlow, MiniMap, useNodesState, useEdgesState, MarkerType } from "@xyflow/react"
import Section from "./Section"
import PSE from "./PSE"
import Parse from "parse"
import { getLastClickTime } from "../Tooltip"
import PseEdge from "./PseEdge"

const SECTION_HEIGHT = 50
const PSE_INDENT = 56
const PSE_WIDTH = 300
const PSE_HEIGHT = 50
const MARGIN_TOP = 10
const PSE_MARGIN_LEFT = 64

export default function FlowProductionItem({recipe, productionItem, onPseSelected}) {
    const [sections, setSections] = React.useState([])

    const [nodes, setNodes /*, onNodesChange*/] = useNodesState([])
    const [edges, setEdges /*, onEdgesChange*/] = useEdgesState([])
    const [ highlightedEdges, setHighlightedEdges ] = React.useState(null) // a Set

    useEffect(() => {
        const load = async () => {
            const pses = await new Parse.Query("ProductionStepExecutions")
                .equalTo("productionItems", productionItem)
                .notEqualTo("deleted", true) // we only want non-deleted PSEs
                .select("sectionName", "ulteriorStep", "status", "deleted",
                    "productionStepSnapshot.name", "productionStepSnapshot.stepNumber",
                    "productionDate", "stepProductionDate",
                    "original"
                )
                .find()
            pses.sort((pse1, pse2) => pse1.get("productionStepSnapshot").stepNumber - pse2.get("productionStepSnapshot").stepNumber)

            //-------- sections --------//
            const sectionNames = new Set()
            pses.forEach(pse => sectionNames.add(pse.get("sectionName")))
            const sections = Array.from(sectionNames).map(name => ({
                name,
                pses: [],
                pseLevels: [] // array of Sets, each Set contains the pses of a level
            }))

            //-------- pse distribution in sections --------//
            pses.forEach(pse => {
                const sectionName = pse.get("sectionName")
                const section = sections.find(s => s.name === sectionName)
                section.pses.push(pse)
            })

            //-------- pses grouped by level for each section --------//
            sections.forEach(section => {
                //-- pseById--//
                const pseById = new Map()
                section.pses.forEach(pse => {
                    pseById.set(pse.id, pse)
                })


                //---- level 0 ----//
                // pseByUlteriorStepId
                const pseByUlteriorStepId = new Map()
                section.pses.forEach(pse => {
                    const ulteriorStep = pse.get("ulteriorStep")
                    if (ulteriorStep) {
                        pseByUlteriorStepId.set(ulteriorStep.id, pse)
                    }
                })

                const psesUlteriorOfNone = new Set(section.pses.filter(pse => !pseByUlteriorStepId.get(pse.id)))
                section.pseLevels.push(psesUlteriorOfNone)

                //---- all next levels ----//
                let currentLevelPses = psesUlteriorOfNone
                let noMoreUlteriorSteps = false
                while (!noMoreUlteriorSteps) {
                    noMoreUlteriorSteps = true
                    const nextLevelPses = new Set()
                    currentLevelPses.forEach(pse => {
                        const ulteriorStep = pseById.get(pse.get("ulteriorStep")?.id)
                        if (ulteriorStep) {
                            // Was it already in a lower level (because of a lower previous step) ?
                            // No need to overoptimize
                            section.pseLevels.forEach(pses => {
                                // We want  to remove the ulteriorStep if it was already in the Set.
                                // It's a lot simpler with a Set than with an Array.
                                pses.forEach(pse => {
                                    if (pse === ulteriorStep) {
                                        pses.delete(pse)
                                    }
                                })
                            })
                            nextLevelPses.add(ulteriorStep)
                            noMoreUlteriorSteps = false // at least one ulterior step was found
                        }
                    })
                    if (nextLevelPses.size >= 0) {
                        section.pseLevels.push(nextLevelPses)
                    }
                    currentLevelPses = nextLevelPses
                }

            })
            setSections(sections)
        }
        if (productionItem) {
            load()
        }
    }, [productionItem])


    //--------------------------------------//
    //-------- Flow nodes and edges --------//
    //--------------------------------------//
    // edgesByPse (used to highlight the edges on the pse hover)
    const edgesByNodeId = useRef(new Map())
    const addEdgeByNodeId = useCallback((edge, nodeId) => {
        let edges = edgesByNodeId.current.get(nodeId)
        if (!edges) {
            edges = new Set()
            edgesByNodeId.current.set(nodeId, edges)
        }
        edges.add(edge)
    }, [])
    useMemo(() => {
        if (!sections.length) {
            return
        }
        const nodes = []
        const edges = []
        let currentSectionY = 0

        sections.forEach(section => {
            //--------------------------------//
            //------------ Section -----------//
            //--------------------------------//
            const size = {width: 300, height: SECTION_HEIGHT}
            const sectionNode = {
                id: section.name,
                type: "section",
                data: {
                    label: section.name,
                    size,
                    id: section.name
                },
                position: {x: 0, y: currentSectionY},
                measured: size,
            }
            nodes.push(sectionNode)
            currentSectionY += SECTION_HEIGHT + MARGIN_TOP
            const startPseY = currentSectionY


            //--------------------------------//
            //------------- PSEs -------------//
            //--------------------------------//
            let level = 0
            section.pseLevels.forEach(pses => {
                // x and y
                const x = PSE_INDENT + level * (PSE_WIDTH + PSE_MARGIN_LEFT)
                let currentY = startPseY

                pses.forEach(pse => {
                    //---- pseNode ----//
                    const size = {width: PSE_WIDTH, height: PSE_HEIGHT}
                    const pseNode = {
                        id: pse.id,
                        type: "pse",
                        data: {
                            label: pse.get("productionStepSnapshot").name,
                            size,
                            id: pse.id,
                            level,
                            status: pse.get("status"),
                            sameDay: pse.get("productionDate") === pse.get("stepProductionDate"),
                            isReprod: !!pse.get("original")
                        },
                        position: {x: x, y: currentY},
                        measured: size
                    }
                    nodes.push(pseNode)

                    //---- edge to ulteriorStep ----//
                    // we can create an edge even if the node of the ulteriorStep is not yet created
                    const ulteriorStep = pse.get("ulteriorStep")
                    if (ulteriorStep) {
                        const edge = {
                            id: pse.id + "-" + ulteriorStep.id,
                            type: "pse",
                            source: pse.id,
                            sourceHandle: "right",
                            target: ulteriorStep.id,
                            targetHandle: "left",
                            markerEnd: {
                                type: MarkerType.ArrowClosed,
                            },
                            data: { highlighted: true }
                        }
                        edges.push(edge)
                        addEdgeByNodeId(edge, pse.id) // for the previous pse
                        addEdgeByNodeId(edge, ulteriorStep.id) // for the ulterior step
                    }

                    currentY += PSE_HEIGHT + MARGIN_TOP
                })
                currentSectionY = Math.max(currentSectionY, currentY)
                level++
            })
        })
        setNodes(nodes)
        setEdges(edges)
    }, [sections])

    const nodeTypes = {
        section: Section,
        pse: PSE
    }

    const edgeTypes = {
        pse: PseEdge
    }

    const onNodeClick = useCallback((event, node) => {
        if (node.type === "pse") {
            setTimeout(() => {
                // was the Tooltip clicked? (happens before and can't be stopped by e.stopPropagation())
                const now = Date.now()
                if (getLastClickTime() > (now - 300)) {
                    return
                }
                onPseSelected(node.id)
            }, 200)
        }
    }, [onPseSelected])

    const onNodeMouseEnter = useCallback((event, node) => {
        const edges = edgesByNodeId.current.get(node.id)
        setHighlightedEdges(edges)
    }, [])

    const onNodeMouseLeave = useCallback(() => {
        setHighlightedEdges(null)
    }, [])

    return (
        <div className="flexColumn stretch flex1">
            <h1>Production de la recette {recipe.uniqueCode} - {recipe.name}</h1>
            <ReactFlow
                nodes={nodes}
                //onNodesChange={onNodesChange}
                edges={edges.map(edge => ({
                    ...edge,
                    data: { highlighted: !!highlightedEdges?.has(edge) }
                }))}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                onNodeClick={onNodeClick}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseLeave={onNodeMouseLeave}
                zoomOnScroll={false}
                zoomOnPinch={false}
                zoomOnDoubleClick={false}
            >
                <MiniMap zoomable pannable/>
            </ReactFlow>
        </div>
    )
}